/* eslint-disable i18next/no-literal-string -- const definitions are the source of truth */
import { EnumValue } from '@/components/shared/EnumValue'
import { BadgeVariant } from '@/components/ui/badge'
import type { useI18n } from '@/i18n'

type I18n = ReturnType<typeof useI18n>

/**
 * Enforcement mode for relationship schema validation. Wire values match the
 * backend {@code EnforcementMode} enum. Each constant owns its badge label
 * (the long "Enforcement: X — …" sentence), its short label (the value shown
 * inside the mode-picker Select), and its description paragraph — so the
 * banner, the Select options, and any future UI that renders a mode all
 * iterate {@link EnforcementMode.values} instead of hand-coding the three
 * variants at every call site.
 */
export class EnforcementMode extends EnumValue {
    constructor(
        code: string,
        variant: BadgeVariant,
        labelFn: (i18n: I18n) => string,
        public readonly shortLabelFn: (i18n: I18n) => string,
        public readonly descriptionFn: (i18n: I18n) => string,
    ) {
        super(code, variant, labelFn)
    }

    shortLabel(i18n: I18n): string {
        return this.shortLabelFn(i18n)
    }

    description(i18n: I18n): string {
        return this.descriptionFn(i18n)
    }

    static readonly STRICT = new EnforcementMode(
        'STRICT',
        BadgeVariant.SUCCESS,
        (i18n) => i18n.partyRelationshipSchema.enforcement.strict as string,
        (i18n) => i18n.partyRelationshipSchema.enforcement.modeStrict as string,
        (i18n) => i18n.partyRelationshipSchema.enforcement.strictDescription as string,
    )
    static readonly WARN = new EnforcementMode(
        'WARN',
        BadgeVariant.WARNING,
        (i18n) => i18n.partyRelationshipSchema.enforcement.warn as string,
        (i18n) => i18n.partyRelationshipSchema.enforcement.modeWarn as string,
        (i18n) => i18n.partyRelationshipSchema.enforcement.warnDescription as string,
    )
    static readonly OFF = new EnforcementMode(
        'OFF',
        BadgeVariant.DESTRUCTIVE,
        (i18n) => i18n.partyRelationshipSchema.enforcement.off as string,
        (i18n) => i18n.partyRelationshipSchema.enforcement.modeOff as string,
        (i18n) => i18n.partyRelationshipSchema.enforcement.offDescription as string,
    )

    // Ordered for the picker Select: least-strict first.
    static readonly values: ReadonlyArray<EnforcementMode> = [
        EnforcementMode.OFF,
        EnforcementMode.WARN,
        EnforcementMode.STRICT,
    ]

    static fromCode(code: string | null | undefined): EnforcementMode | undefined {
        return EnforcementMode.values.find((v) => v.code === code)
    }
}
